import React, { useEffect } from 'react';
import { Form, Input, Button, Card, Typography, Space, Spin, message } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useCompany, useCreateCompany, useUpdateCompany } from '@/hooks/useCompanies';
import { extractErrorMessage } from '@/utils/helpers';

const { Title } = Typography;
const { TextArea } = Input;

const CompanyForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  
  const { data: company, isLoading: isLoadingCompany } = useCompany(id || '');
  const createCompany = useCreateCompany();
  const updateCompany = useUpdateCompany();

  useEffect(() => {
    if (company) {
      form.setFieldsValue(company);
    }
  }, [company, form]);

  const onFinish = async (values: any) => {
    try {
      if (id) {
        await updateCompany.mutateAsync({ id, data: values });
        message.success('Empresa atualizada com sucesso!');
      } else {
        await createCompany.mutateAsync(values);
        message.success('Empresa criada com sucesso!');
      }
      navigate('/companies');
    } catch (error: any) {
      message.error(extractErrorMessage(error, 'Erro ao salvar empresa'));
    }
  };

  const isLoading = createCompany.isPending || updateCompany.isPending;

  return (
    <div>
      <Title level={4}>{id ? 'Editar Empresa' : 'Nova Empresa'}</Title>
      
      <Card>
        <Spin spinning={isLoadingCompany}>
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={{
              max_users: 10,
              max_devices: 50,
            }}
          >
            <Form.Item
              name="name"
              label="Nome da Empresa"
              rules={[{ required: true, message: 'Por favor, insira o nome da empresa' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="trade_name"
              label="Nome Fantasia"
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="cnpj"
              label="CNPJ"
              rules={[{ required: true, message: 'Por favor, insira o CNPJ' }]}
            >
              <Input placeholder="00.000.000/0000-00" />
            </Form.Item>

            <Form.Item
              name="email"
              label="E-mail"
              rules={[
                { required: true, message: 'Por favor, insira o e-mail' },
                { type: 'email', message: 'E-mail inválido' },
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="phone"
              label="Telefone"
            >
              <Input placeholder="(00) 0000-0000" />
            </Form.Item>

            <Form.Item
              name="address"
              label="Endereço"
            >
              <TextArea rows={3} />
            </Form.Item>

            <Form.Item
              name="max_users"
              label="Máximo de Usuários"
            >
              <Input type="number" min={1} />
            </Form.Item>

            <Form.Item
              name="max_devices"
              label="Máximo de Dispositivos"
            >
              <Input type="number" min={1} />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={isLoading}>
                  Salvar
                </Button>
                <Button onClick={() => navigate('/companies')}>
                  Cancelar
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Spin>
      </Card>
    </div>
  );
};

export default CompanyForm;
